import React, { memo } from 'react';
import { Box, Button, IconButton, Paper, Skeleton, Tooltip, Typography, alpha } from '@mui/material';
import { Add as AddIcon, EditOutlined as EditIcon, DeleteOutline as DeleteIcon } from '@mui/icons-material';
import { Chapter } from './types';

interface ChapterSidebarProps {
  chapters: Chapter[];
  selectedChapterId: string | null;
  loading: boolean;
  canWrite: boolean;
  onSelectChapter: (chapter: Chapter) => void;
  onAddChapter: () => void;
  onEditChapter: (chapter: Chapter) => void;
  onDeleteChapter: (chapterId: string) => void;
}

/** Height of the app's fixed top bar. */
export const TOP_BAR = 56;
/** Where pinned panels sit: just under the top bar, with breathing room. */
export const STICKY_TOP = TOP_BAR + 16;

/**
 * The bank's chapters. It stays pinned while the questions scroll past, and
 * scrolls on its own when a bank has more chapters than fit on screen.
 */
const ChapterSidebar = memo<ChapterSidebarProps>(({
  chapters,
  selectedChapterId,
  loading,
  canWrite,
  onSelectChapter,
  onAddChapter,
  onEditChapter,
  onDeleteChapter,
}) => (
  <Paper
    variant="outlined"
    sx={{
      position: { md: 'sticky' },
      top: STICKY_TOP,
      maxHeight: { md: `calc(100vh - ${STICKY_TOP + 16}px)` },
      display: 'flex',
      flexDirection: 'column',
      borderRadius: '14px',
      overflow: 'hidden',
    }}
  >
    <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
      <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.08em', color: 'text.secondary', lineHeight: 1.5 }}>
        Chapters
      </Typography>
      {!loading && (
        <Typography variant="caption" color="text.disabled">{chapters.length}</Typography>
      )}
    </Box>

    <Box sx={{ flex: 1, overflowY: 'auto', px: 1, pb: 1 }}>
      {loading ? (
        [0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={40} sx={{ my: 0.5, borderRadius: '10px' }} />)
      ) : chapters.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 2 }}>
          No chapters yet.
        </Typography>
      ) : (
        chapters.map((chapter) => {
          const active = selectedChapterId === chapter._id;
          return (
            <Box
              key={chapter._id}
              role="button"
              tabIndex={0}
              onClick={() => onSelectChapter(chapter)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectChapter(chapter); } }}
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.25,
                py: 0.9,
                my: 0.25,
                borderRadius: '10px',
                cursor: 'pointer',
                outline: 'none',
                transition: 'background-color 120ms',
                bgcolor: active ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                '&:hover': { bgcolor: active ? alpha(theme.palette.primary.main, 0.12) : 'action.hover' },
                '&:focus-visible': { boxShadow: `inset 0 0 0 2px ${alpha(theme.palette.primary.main, 0.5)}` },
                '&:hover .ch-actions, &:focus-within .ch-actions': { display: 'flex' },
                '&:hover .ch-count, &:focus-within .ch-count': { display: canWrite ? 'none' : 'block' },
              })}
            >
              <Typography
                variant="caption"
                sx={{ width: 22, flexShrink: 0, fontWeight: 700, color: active ? 'primary.main' : 'text.disabled' }}
              >
                {chapter.order}
              </Typography>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  noWrap
                  sx={{ fontWeight: active ? 700 : 500, color: active ? 'primary.main' : 'text.primary' }}
                >
                  {chapter.name}
                </Typography>
                {chapter.description && (
                  <Typography variant="caption" color="text.secondary" noWrap component="div">
                    {chapter.description}
                  </Typography>
                )}
              </Box>
              <Typography className="ch-count" variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, flexShrink: 0 }}>
                {chapter.questionCount || 0}
              </Typography>
              {canWrite && (
                <Box className="ch-actions" sx={{ display: 'none', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="Rename chapter">
                    <IconButton size="small" onClick={() => onEditChapter(chapter)} sx={{ p: 0.4, color: 'text.secondary' }}>
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete chapter">
                    <IconButton
                      size="small"
                      onClick={() => onDeleteChapter(chapter._id)}
                      sx={{ p: 0.4, color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          );
        })
      )}
    </Box>

    {canWrite && (
      <Box sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button fullWidth size="small" startIcon={<AddIcon />} onClick={onAddChapter} sx={{ justifyContent: 'flex-start', px: 1.5, borderRadius: '10px', textTransform: 'none' }}>
          Add chapter
        </Button>
      </Box>
    )}
  </Paper>
));

ChapterSidebar.displayName = 'ChapterSidebar';

export default ChapterSidebar;
